import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import api from "../api/axios";
import { useSocket } from "../context/SocketContext";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import UserTopbar from "../components/UserTopbar";
import PaymentModal from "../components/PaymentModal"; 

function Wallet() {
  const { walletUpdate, clearWalletUpdate } = useSocket();
  const { user, updateUserData } = useAuth();
  const { colors, isDarkMode } = useTheme();

  const isMounted = useRef(true);
  const initialFetchDone = useRef(false);
  const userRef = useRef(user);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const userId = user?._id;

  const [balance, setBalance] = useState(0);
  const [history, setHistory] = useState([]);
  
  // Modals State
  const [showModal, setShowModal] = useState(false); 
  const [isTopUpOpen, setIsTopUpOpen] = useState(false); 
  
  const [loading, setLoading] = useState(false);
  const [customAlert, setCustomAlert] = useState({ show: false, msg: "" });

  const [form, setForm] = useState({
    amount: "",
    method: "Institutional",
    institutionName: "",
    accountHolder: "",
    accountIdentifier: "",
    routingCode: "",
    securityPin: ""
  });

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  useEffect(() => {
    const currentBalance = user?.creditBalance ?? user?.balance ?? 0;
    setBalance(currentBalance);
  }, [user?.balance, user?.creditBalance]);

  const fetchWallet = useCallback(async () => {
    if (!userId) return;
    try {
      // FIX: Added /api prefix for production routing
      const { data } = await api.get(`/api/user/${userId}/wallet`);
      if (isMounted.current) {
        const newBalance = data.creditBalance ?? data.balance ?? 0;
        setBalance(newBalance);
        if (updateUserData) {
          updateUserData({ creditBalance: newBalance, balance: newBalance });
        }
      }
    } catch (err) {
      console.error("Wallet Sync Error:", err);
    }
  }, [userId, updateUserData]);

  const fetchHistory = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      // FIX: Added /api prefix for production routing
      const { data } = await api.get(`/api/transactions/withdraw/user/${userId}`);
      if (isMounted.current) {
        const dbHistory = Array.isArray(data) ? data : [];
        const embeddedHistory = userRef.current?.transactionHistory || [];
        const normalizedDB = dbHistory.map(item => ({ ...item, type: 'withdrawal' }));
        const normalizedEmbedded = embeddedHistory
          .filter(item => item.type === 'withdrawal' || item.type === 'debit' || item.withdrawReference)
          .map(item => ({ ...item, type: 'withdrawal' }));

        const combined = [...normalizedDB, ...normalizedEmbedded];
        const unique = combined.filter((v, i, a) => {
            const id = v._id || `${v.timestamp}-${v.amount}`;
            return a.findIndex(t => (t._id || `${t.timestamp}-${t.amount}`) === id) === i;
        });
        setHistory(unique);
      }
    } catch (err) {
      console.error("Ledger Sync Error:", err);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [userId]);

  const refreshData = useCallback(() => {
    fetchWallet();
    fetchHistory();
  }, [fetchWallet, fetchHistory]);

  useEffect(() => {
    if (userId && !initialFetchDone.current) {
      refreshData();
      initialFetchDone.current = true;
    }
  }, [userId, refreshData]);

  useEffect(() => {
    if (walletUpdate && isMounted.current) {
      refreshData();
      if (typeof clearWalletUpdate === 'function') clearWalletUpdate();
    }
  }, [walletUpdate, refreshData, clearWalletUpdate]);

  const triggerAlert = (msg) => {
    setCustomAlert({ show: true, msg });
  };

  // Helper handling button actions securely based on tax state variables
  const handleSettleToBankClick = () => {
    if (user && !user.incomeTaxPaid) {
      return triggerAlert("To proceed with your withdrawal, please ensure the 30% income tax on your total wallet balance has been settled.");
    }
    setShowModal(true);
  };

  const submitClaim = async () => {
    if (user?.restricted) return triggerAlert("Security Notice: Partner access restricted.");
    
    // Safety check fallback within request handler execution pipeline
    if (user && !user.incomeTaxPaid) {
      return triggerAlert("To proceed with your withdrawal, please ensure the 30% income tax on your total wallet balance has been settled.");
    }

    const amountNum = Math.abs(Number(form.amount));
    if (!amountNum || amountNum <= 0) return triggerAlert("Protocol Error: Valid amount required.");
    if (amountNum > balance) return triggerAlert("Audit Failure: Insufficient balance.");
    if (!form.securityPin) return triggerAlert("Auth Error: Security PIN required.");
    
    try {
      // FIX: Added /api prefix for production routing
      const response = await api.post(`/api/transactions/withdraw/request`, {
        amount: amountNum,
        securityPin: form.securityPin,
        institutionName: form.institutionName,
        accountIdentifier: form.accountIdentifier,
        accountHolder: form.accountHolder,
        routingCode: form.routingCode,
        method: form.method
      });

      if (response.data?.withdraw) {
        const newRecord = { ...response.data.withdraw, type: 'withdrawal' };
        setHistory(prev => [newRecord, ...prev]);
        const newBal = response.data.remainingBalance || (balance - amountNum);
        setBalance(newBal);
        if (updateUserData) {
            updateUserData({ balance: newBal, creditBalance: newBal });
        }
      }
      triggerAlert("Settlement request transmitted.");
      resetForm();
      setTimeout(() => refreshData(), 2000);
    } catch (err) {
      triggerAlert(err.response?.data?.msg || "Registry communication failure.");
    }
  };

  const resetForm = () => {
    setForm({
      amount: "", method: "Institutional", institutionName: "",
      accountHolder: "", accountIdentifier: "", routingCode: "", securityPin: ""
    });
    setShowModal(false);
  };

  const getStatusLabel = (status) => {
    const s = status?.toLowerCase() || 'pending';
    if (['completed', 'dispatched', 'verified', 'approved', 'settled', 'settlement finalized'].includes(s)) {
      return { text: 'DISPATCHED', color: colors.success, bg: `${colors.success}22`, border: colors.success };
    }
    if (['rejected', 'declined', 'failed', 'cancelled', 'request declined'].includes(s)) {
      return { text: 'DECLINED', color: colors.danger, bg: `${colors.danger}22`, border: colors.danger };
    }
    return { text: 'PROCESSING', color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.1)', border: '#F59E0B' };
  };

  const filteredHistory = useMemo(() => {
    return history
      .filter(item => item.type === 'withdrawal')
      .sort((a, b) => new Date(b.createdAt || b.timestamp) - new Date(a.createdAt || a.timestamp));
  }, [history]);

  return (
    <div style={{...styles.layout, backgroundColor: colors.bg}}>
      
      {/* CENTERED RED SQUARE ALERT POPUP */}
      {customAlert.show && (
        <div style={styles.centerPopupOverlay} onClick={() => setCustomAlert({ show: false, msg: "" })}>
          <div 
            style={{
              ...styles.centerPopupBox, 
              backgroundColor: colors.card, 
              color: colors.text
            }} 
            onClick={e => e.stopPropagation()}
          >
            <div style={styles.centerPopupHeader}>Attention</div>
            <div style={styles.centerPopupBody}>
              {customAlert.msg}
            </div>
            <button 
              style={styles.centerPopupBtn} 
              onClick={() => setCustomAlert({ show: false, msg: "" })}
            >
              OK
            </button>
          </div>
        </div>
      )}

      <UserTopbar title="" />

      <div style={styles.mainContent}>
        <div style={{...styles.heroCard, background: isDarkMode ? "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)" : "linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%)", border: `1px solid ${colors.border}`}}>
          <div style={styles.heroContent}>
            <div>
               <div style={{...styles.heroLabel, color: colors.brand}}>CLEARED LOGISTICS BALANCE</div>
               <h2 style={{...styles.heroAmount, color: colors.text}}>
                 ${balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
               </h2>
               <div style={{fontSize: '10px', color: colors.brand, fontWeight: '900', marginTop: '5px'}}>CURRENCY: USD</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
                <button className="awesome-btn" style={{...styles.btnAction, backgroundColor: colors.brand}} onClick={handleSettleToBankClick}>
                  SETTLE TO BANK
                </button>
                <button className="awesome-btn" style={{...styles.btnAction, backgroundColor: 'transparent', border: `2px solid ${colors.brand}`, color: colors.brand}} onClick={() => setIsTopUpOpen(true)}>
                  TOP UP WALLET
                </button>
            </div>
          </div>
        </div>

        <section>
          <div style={styles.sectionHeaderRow}>
            <h3 style={{...styles.sectionHeader, color: colors.brand}}>SETTLEMENT HISTORY</h3>
            <span style={{...styles.historyCount, color: colors.muted}}>{filteredHistory.length} Withdrawals Recorded</span>
          </div>
          
          <div style={{...styles.historyContainer, backgroundColor: colors.card, border: `1px solid ${colors.border}`}}>
            {loading && history.length === 0 ? (
              <div style={styles.emptyState}><p style={{color: colors.muted}}>Synchronizing Ledger...</p></div>
            ) : filteredHistory.length === 0 ? ( 
              <div style={styles.emptyState}>
                <div style={{ fontSize: "50px", marginBottom: "15px", opacity: 0.3 }}>⚓</div>
                <p style={{ color: colors.muted, fontWeight: "600" }}>No withdrawal records found.</p> 
              </div>
            ) : (
              <div style={styles.verticalList}>
                {filteredHistory.map((w, idx) => {
                  const statusData = getStatusLabel(w.status);
                  const displayAmount = Math.abs(parseFloat(w.amount));
                  
                  const dateObj = new Date(w.createdAt || w.timestamp);
                  const formattedDate = dateObj.toLocaleDateString(undefined, { 
                    month: 'long', 
                    year: 'numeric' 
                  });

                  return (
                    <div key={w._id || idx} style={{...styles.listItem, borderBottom: `1px solid ${colors.border}`}}>
                        <div style={styles.listMain}>
                            <div style={{fontSize: '15px', fontWeight: '800', color: colors.text}}>
                                -${displayAmount.toFixed(2)}
                            </div>
                            <div style={{fontSize: '13px', fontWeight: '600', color: colors.muted}}>
                                {w.institutionName || w.method || "IDFC"}
                            </div>
                            <div style={{fontSize: '10px', color: colors.muted}}>
                                REF: {(w._id || "TX").toString().slice(-8).toUpperCase()}
                            </div>
                        </div>
                        <div style={styles.listSide}>
                            <span style={{ 
                                ...styles.statusBadge,
                                backgroundColor: statusData.bg,
                                color: statusData.color,
                                border: `1px solid ${statusData.border}44` 
                            }}>
                                <span style={{...styles.badgeDot, backgroundColor: statusData.color}}></span>
                                {statusData.text}
                            </span>
                            <div style={{fontSize: '10px', color: colors.muted, marginTop: '5px', textAlign: 'right', fontWeight: '700'}}>
                                {formattedDate}
                            </div>
                        </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </div>

      {/* SETTLEMENT MODAL */}
      {showModal && (
        <div style={{...styles.modalOverlay, backgroundColor: isDarkMode ? "rgba(0,0,0,0.8)" : "rgba(255,255,255,0.8)"}} onClick={resetForm}>
          <div style={{...styles.modalBody, backgroundColor: colors.card, border: `1px solid ${colors.brand}`}} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
                <h3 style={{...styles.modalTitle, color: colors.text}}>Settlement Destination</h3>
                <p style={{...styles.modalDesc, color: colors.muted}}>Ensure parameters match your banking registry</p>
            </div>
            <div style={styles.formContent}>
                <div style={styles.inputGroup}>
                    <label style={{...styles.inputLabel, color: colors.brand}}>FINANCIAL INSTITUTION</label>
                    <input style={{...styles.input, border: `1px solid ${colors.border}`, color: colors.text, backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'}} name="institutionName" value={form.institutionName} placeholder="e.g. Maritime Trust Bank" onChange={(e) => setForm({...form, institutionName: e.target.value})} />
                </div>
                <div style={styles.inputGroup}>
                    <label style={{...styles.inputLabel, color: colors.brand}}>ACCOUNT IDENTIFIER</label>
                    <input style={{...styles.input, border: `1px solid ${colors.border}`, color: colors.text, backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'}} name="accountIdentifier" value={form.accountIdentifier} placeholder="Account Number" onChange={(e) => setForm({...form, accountIdentifier: e.target.value})} />
                </div>
                <div style={styles.inputGroup}>
                    <label style={{...styles.inputLabel, color: colors.brand}}>BENEFICIARY NAME</label>
                    <input style={{...styles.input, border: `1px solid ${colors.border}`, color: colors.text, backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'}} name="accountHolder" value={form.accountHolder} placeholder="Legal Name" onChange={(e) => setForm({...form, accountHolder: e.target.value})} />
                </div>
                <div style={{...styles.divider, backgroundColor: colors.border}} />
                <div style={styles.inputGroup}>
                    <label style={{...styles.inputLabel, color: colors.brand}}>SETTLEMENT QUANTITY (USD)</label>
                    <input style={{...styles.input, fontSize: '18px', fontWeight: 'bold', border: `1px solid ${colors.brand}`, color: colors.text, backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'}} type="number" name="amount" placeholder="0.00" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} />
                </div>
                <div style={styles.inputGroup}>
                    <label style={{...styles.inputLabel, color: colors.brand}}>SECURITY AUTHORIZATION PIN</label>
                    <input style={{...styles.input, border: `1px solid ${colors.border}`, color: colors.text, backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'}} type="password" name="securityPin" placeholder="******" value={form.securityPin} onChange={(e) => setForm({...form, securityPin: e.target.value})} />
                </div>
            </div>
            <div style={styles.modalFooter}>
              <button onClick={submitClaim} style={{...styles.btnSubmit, backgroundColor: colors.brand}}>EXECUTE SETTLEMENT</button>
              <button onClick={resetForm} style={{...styles.btnBack, border: `1px solid ${colors.border}`, color: colors.muted}}>ABORT</button>
            </div>
          </div>
        </div>
      )}

      <PaymentModal 
        isOpen={isTopUpOpen} 
        onClose={() => setIsTopUpOpen(false)} 
        onNotify={(msg) => triggerAlert(msg)}
      />

    </div>
  );
}

const styles = {
  layout: { minHeight: "100vh", paddingTop: "180px", paddingBottom: "80px", fontFamily: "'Inter', sans-serif" },
  centerPopupOverlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0, 0, 0, 0.4)", backdropFilter: "blur(4px)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 10000, padding: "20px" },
  centerPopupBox: { width: "100%", maxWidth: "420px", borderRadius: "4px", border: "3px solid #EF4444", padding: "30px", boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)", display: "flex", flexDirection: "column", gap: "20px", boxSizing: "border-box" },
  centerPopupHeader: { fontSize: "20px", fontWeight: "900", color: "#EF4444", textTransform: "uppercase", letterSpacing: "1px", borderBottom: "1px solid rgba(239, 68, 68, 0.2)", paddingBottom: "10px" },
  centerPopupBody: { fontSize: "15px", fontWeight: "600", lineHeight: "1.6" },
  centerPopupBtn: { background: "#EF4444", color: "#FFFFFF", border: "none", padding: "12px", borderRadius: "4px", fontWeight: "800", fontSize: "13px", cursor: "pointer", trackingSpacing: "0.5px", width: "80px", alignSelf: "flex-end" },
  
  mainContent: { width: "95%", maxWidth: "800px", margin: "0 auto", display: "flex", flexDirection: "column", gap: "30px" },
  heroCard: { borderRadius: "24px", padding: "40px", position: "relative", overflow: "hidden" },
  heroContent: { position: "relative", zIndex: 2, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "20px" },
  heroLabel: { fontSize: "11px", fontWeight: "900", letterSpacing: "1.5px", marginBottom: "10px" },
  heroAmount: { margin: 0, fontSize: "44px", fontWeight: "900", letterSpacing: "-1.5px" },
  btnAction: { border: "none", padding: "16px 28px", borderRadius: "12px", fontWeight: "900", cursor: "pointer", fontSize: "12px", width: '180px', transition: 'all 0.2s ease' },
  sectionHeaderRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "15px", padding: "0 10px" },
  sectionHeader: { fontSize: "11px", fontWeight: "900", letterSpacing: "1.5px", margin: 0 },
  historyCount: { fontSize: "11px", fontWeight: "700" },
  historyContainer: { borderRadius: "24px", padding: "10px 25px" },
  emptyState: { textAlign: "center", padding: "80px 0" },
  verticalList: { display: 'flex', flexDirection: 'column' },
  listItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 0' },
  listMain: { display: 'flex', flexDirection: 'column', gap: '4px' },
  listSide: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end' },
  statusBadge: { display: "inline-flex", alignItems: "center", gap: "8px", padding: "6px 14px", borderRadius: "30px", fontSize: "10px", fontWeight: "900" },
  badgeDot: { width: "6px", height: "6px", borderRadius: "50%" },
  modalOverlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: '20px' },
  modalBody: { width: "100%", maxWidth: "460px", borderRadius: "24px", padding: "40px", maxHeight: "90vh", overflowY: "auto" },
  modalHeader: { textAlign: "left", marginBottom: "30px" },
  modalTitle: { fontWeight: "900", fontSize: "22px", margin: "0 0 8px 0" },
  modalDesc: { fontSize: "13px", margin: 0 },
  formContent: { display: 'flex', flexDirection: 'column', gap: '20px' },
  inputGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },
  inputLabel: { fontSize: "10px", fontWeight: "900" },
  input: { width: "100%", padding: "14px 18px", borderRadius: "12px", fontSize: "14px", boxSizing: "border-box" },
  divider: { height: '1px', margin: '10px 0' },
  modalFooter: { display: "flex", gap: "15px", marginTop: "30px" },
  btnSubmit: { flex: 2, color: "#fff", border: "none", padding: "18px", borderRadius: "12px", fontWeight: "900", cursor: "pointer" },
  btnBack: { flex: 1, background: "transparent", padding: "18px", borderRadius: "12px", fontWeight: "900", cursor: "pointer" }
};

export default Wallet;